import { useState, useRef, useEffect } from "react";
import { http } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { CheckCircle2, RotateCcw } from "lucide-react";

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export default function GuestRelease() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cfg, setCfg] = useState(null);
  const sigRef = useRef(null);

  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "", city: "", state: "", date: today,
    recording_types: [],
    story_agree: false, editing_agree: false, compensation_agree: false,
    responsibility_agree: false, liability_agree: false,
    privacy_preference: "",
    anonymous_options: [],
    sensitive_agree: false,
    receive_copy: "no", notify_live: "no", future_projects: "no",
    emergency_name: "", emergency_phone: "", emergency_relationship: "",
    typed_name: "", final_agree: false,
  });

  useEffect(() => {
    http.get("/guest-release/form-settings").then(r => setCfg(r.data)).catch(() => setCfg({}));
  }, []);

  const sec = (key) => cfg?.sections?.[key]?.enabled !== false;
  const secText = (key) => cfg?.sections?.[key]?.text || "";
  const secTitle = (key) => cfg?.sections?.[key]?.title || "";
  const fld = (key) => cfg?.fields?.[key] !== false;

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setForm(p => ({
    ...p, [k]: p[k].includes(v) ? p[k].filter(x => x !== v) : [...p[k], v]
  }));

  const validate = () => {
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("Please complete all required guest information fields."); return false;
    }
    if (fld("phone") && !form.phone) { toast.error("Please enter your phone number."); return false; }
    if (fld("city") && !form.city) { toast.error("Please enter your city."); return false; }
    if (fld("state") && !form.state) { toast.error("Please select your state."); return false; }
    if (sec("permission_to_record") && form.recording_types.length === 0) { toast.error("Select at least one recording type."); return false; }
    if (sec("permission_to_use_story") && !form.story_agree) { toast.error("Please agree to Permission to Use My Story."); return false; }
    if (sec("editing") && !form.editing_agree) { toast.error("Please agree to the Editing section."); return false; }
    if (sec("compensation") && !form.compensation_agree) { toast.error("Please agree to the Compensation section."); return false; }
    if (sec("responsibility") && !form.responsibility_agree) { toast.error("Please agree to My Responsibility."); return false; }
    if (sec("liability") && !form.liability_agree) { toast.error("Please agree to Release of Liability."); return false; }
    if (sec("privacy_preference") && !form.privacy_preference) { toast.error("Please select a privacy preference."); return false; }
    if (sec("sensitive_topics") && !form.sensitive_agree) { toast.error("Please acknowledge the Sensitive Topics section."); return false; }
    if (!form.typed_name) { toast.error("Please type your name for the signature."); return false; }
    if (sigRef.current?.isEmpty()) { toast.error("Please provide your signature."); return false; }
    if (!form.final_agree) { toast.error("Please check the Final Agreement."); return false; }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const sig = sigRef.current.toDataURL("image/png");
      await http.post("/guest-release/submit", { ...form, signature_data: sig });
      setSubmitted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) return (
    <Layout>
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="max-w-lg text-center py-20">
          <CheckCircle2 size={64} className="mx-auto text-black mb-8" />
          <h1 className="font-bold text-3xl text-black mb-4">Thank you for sharing your story.</h1>
          <p className="text-gray-600 text-lg mb-2">Your release has been received.</p>
          <p className="text-gray-600 text-lg mb-8">Mickey will contact you with interview details.</p>
          <div className="border-t-2 border-black pt-6 mt-6">
            <p className="text-xs text-gray-400 tracking-widest uppercase">The Empty Chair™ Podcast</p>
            <p className="text-xs text-gray-400 mt-1">One Chair. One Story. One Conversation.</p>
          </div>
        </div>
      </div>
    </Layout>
  );

  if (!cfg) return (
    <Layout>
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-pulse text-gray-400 text-sm">Loading form...</div>
      </div>
    </Layout>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-white text-black">
        <div className="max-w-3xl mx-auto px-6 py-12 md:py-20">
          {/* Header */}
          <header className="text-center mb-12" data-testid="release-header">
            <h1 className="text-4xl md:text-5xl font-black tracking-tight mb-1">THE EMPTY CHAIR</h1>
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="h-[2px] w-12 bg-black" />
              <span className="text-sm tracking-[0.4em] font-medium">PODCAST</span>
              <div className="h-[2px] w-12 bg-black" />
            </div>
            <p className="text-[10px] tracking-[0.35em] text-gray-500 uppercase mb-8">One Chair. One Story. One Conversation.</p>
            <div className="border-t-2 border-black pt-6">
              <h2 className="text-xl md:text-2xl font-bold tracking-wide mb-2">GUEST PARTICIPATION & MEDIA RELEASE</h2>
              <p className="italic text-gray-600">{cfg.header_subtitle || "Thank you for sharing your story."}</p>
            </div>
            <p className="text-sm text-gray-500 mt-4 max-w-xl mx-auto leading-relaxed">
              {cfg.intro_text || "The purpose of The Empty Chair™ Podcast is to encourage honest conversations that inspire, educate, and help others. By signing below, you acknowledge and agree to the following:"}
            </p>
          </header>

          {/* Guest Information */}
          <Section title="GUEST INFORMATION" testid="section-guest-info">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="First Name *" value={form.first_name} onChange={v => up("first_name", v)} testid="field-first-name" />
              <Field label="Last Name *" value={form.last_name} onChange={v => up("last_name", v)} testid="field-last-name" />
              <Field label="Email *" type="email" value={form.email} onChange={v => up("email", v)} testid="field-email" />
              {fld("phone") && <Field label="Phone Number *" type="tel" value={form.phone} onChange={v => up("phone", v)} testid="field-phone" />}
              {fld("city") && <Field label="City *" value={form.city} onChange={v => up("city", v)} testid="field-city" />}
              {fld("state") && (
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-gray-500 block mb-1.5">State *</label>
                  <select value={form.state} onChange={e => up("state", e.target.value)}
                    className="w-full border-b-2 border-gray-300 bg-transparent py-2.5 text-sm focus:border-black focus:outline-none transition-colors"
                    data-testid="field-state">
                    <option value="">Select...</option>
                    {STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}
            </div>
            {fld("date") && (
              <div className="mt-4 max-w-xs">
                <Field label="Date" type="date" value={form.date} onChange={v => up("date", v)} testid="field-date" />
              </div>
            )}
          </Section>

          {/* Section 1 - Permission to Record */}
          {sec("permission_to_record") && (
            <Section num="1" title={secTitle("permission_to_record") || "PERMISSION TO RECORD"} testid="section-record">
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                {secText("permission_to_record") || "I voluntarily agree to participate in The Empty Chair\u2122 Podcast produced by Mickey Trivett and/or Simple Truths Club."}
              </p>
              <p className="text-sm text-gray-700 mb-3">I understand that my participation may include:</p>
              <div className="space-y-2.5">
                {["Video Recording", "Audio Recording", "Photography"].map(t => (
                  <Checkbox key={t} label={t} checked={form.recording_types.includes(t)}
                    onChange={() => toggleArr("recording_types", t)} testid={`check-record-${t.toLowerCase().replace(" ","-")}`} />
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3 italic">* At least one option must be selected.</p>
            </Section>
          )}

          {/* Section 2 - Permission to Use My Story */}
          {sec("permission_to_use_story") && (
            <Section num="2" title={secTitle("permission_to_use_story") || "PERMISSION TO USE MY STORY"} testid="section-story">
              <p className="text-sm text-gray-700 leading-relaxed mb-3">
                {secText("permission_to_use_story") || "I grant Mickey Trivett, Mirror Conversations\u2122, The Empty Chair\u2122, and Simple Truths Club the irrevocable right to:"}
              </p>
              <ul className="list-disc list-inside text-sm text-gray-700 space-y-1.5 mb-4 ml-2">
                <li>Record my interview.</li>
                <li>Edit my interview for length or clarity.</li>
                <li>Publish all or portions of my interview.</li>
                <li>Use my name, likeness, voice, photographs, and statements.</li>
                <li>Share the content across websites, podcasts, YouTube, social media, streaming platforms, television, promotional materials, books, documentaries, and future media formats.</li>
              </ul>
              <p className="text-sm text-gray-700 mb-4">I understand my interview may remain publicly available indefinitely.</p>
              <Checkbox label="I Agree" checked={form.story_agree} onChange={() => up("story_agree", !form.story_agree)} testid="check-story" bold />
            </Section>
          )}

          {/* Section 3 - Editing */}
          {sec("editing") && (
            <Section num="3" title={secTitle("editing") || "EDITING"} testid="section-editing">
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                {secText("editing") || "I understand the interview may be edited for time, quality, or content while preserving the overall meaning of my statements."}
              </p>
              <Checkbox label="I Understand" checked={form.editing_agree} onChange={() => up("editing_agree", !form.editing_agree)} testid="check-editing" bold />
            </Section>
          )}

          {/* Section 4 - Compensation */}
          {sec("compensation") && (
            <Section num="4" title={secTitle("compensation") || "COMPENSATION"} testid="section-compensation">
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                {secText("compensation") || "I understand my participation is voluntary unless otherwise agreed upon in writing. I do not expect compensation, royalties, or future payment resulting from the publication or distribution of this interview."}
              </p>
              <Checkbox label="I Understand" checked={form.compensation_agree} onChange={() => up("compensation_agree", !form.compensation_agree)} testid="check-compensation" bold />
            </Section>
          )}

          {/* Section 5 - My Responsibility */}
          {sec("responsibility") && (
            <Section num="5" title={secTitle("responsibility") || "MY RESPONSIBILITY"} testid="section-responsibility">
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                {secText("responsibility") || "I understand I am responsible for the statements I make during my interview. I agree not to knowingly provide false, defamatory, or unlawful content."}
              </p>
              <Checkbox label="I Agree" checked={form.responsibility_agree} onChange={() => up("responsibility_agree", !form.responsibility_agree)} testid="check-responsibility" bold />
            </Section>
          )}

          {/* Section 6 - Release of Liability */}
          {sec("liability") && (
            <Section num="6" title={secTitle("liability") || "RELEASE OF LIABILITY"} testid="section-liability">
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                {secText("liability") || "I release Mickey Trivett, Mirror Conversations\u2122, The Empty Chair\u2122, Simple Truths Club, and their representatives from any claims relating to the lawful use of the recorded material as described above."}
              </p>
              <Checkbox label="I Agree" checked={form.liability_agree} onChange={() => up("liability_agree", !form.liability_agree)} testid="check-liability" bold />
            </Section>
          )}

          {/* Section 7 - Privacy Preference */}
          {sec("privacy_preference") && (
            <Section num="7" title={secTitle("privacy_preference") || "PRIVACY PREFERENCE"} testid="section-privacy">
              <p className="text-sm text-gray-700 mb-4">{secText("privacy_preference") || "Please choose one:"}</p>
              <div className="space-y-2.5">
                {[["full_name", "Use my full name"], ["first_only", "First name only"], ["anonymous", "Remain anonymous (voice and/or appearance may be modified when possible)"]].map(([v, l]) => (
                  <Radio key={v} label={l} checked={form.privacy_preference === v}
                    onChange={() => up("privacy_preference", v)} testid={`radio-privacy-${v}`} />
                ))}
              </div>
              {form.privacy_preference === "anonymous" && (
                <div className="mt-4 ml-7 p-4 border border-gray-200 rounded-lg bg-gray-50 space-y-2.5">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Additional anonymous options:</p>
                  {["Hide Face", "Change Voice", "Use Alias Instead", "Allow Audio Only"].map(opt => (
                    <Checkbox key={opt} label={opt} checked={form.anonymous_options.includes(opt)}
                      onChange={() => toggleArr("anonymous_options", opt)} testid={`check-anon-${opt.toLowerCase().replace(/ /g,"-")}`} />
                  ))}
                </div>
              )}
            </Section>
          )}

          {/* Sensitive Topics */}
          {sec("sensitive_topics") && (
            <Section title={secTitle("sensitive_topics") || "SENSITIVE TOPICS ACKNOWLEDGMENT"} testid="section-sensitive">
              <p className="text-sm text-gray-700 leading-relaxed mb-4">
                {secText("sensitive_topics") || "I understand this interview may include discussion of personal experiences, grief, trauma, mental health, relationships, or other sensitive subjects. I understand I may decline to answer any question or stop the interview at any time before its conclusion."}
              </p>
              <Checkbox label="I Understand" checked={form.sensitive_agree} onChange={() => up("sensitive_agree", !form.sensitive_agree)} testid="check-sensitive" bold />
            </Section>
          )}

          {/* Optional Questions */}
          {sec("optional_questions") && (
            <Section title={secTitle("optional_questions") || "OPTIONAL QUESTIONS"} testid="section-optional">
              <div className="space-y-5">
                {fld("opt_receive_copy") && <OptionalYesNo label="Would you like to receive a copy of your interview?" value={form.receive_copy} onChange={v => up("receive_copy", v)} testid="opt-copy" />}
                {fld("opt_notify_live") && <OptionalYesNo label="Would you like to be notified when your episode goes live?" value={form.notify_live} onChange={v => up("notify_live", v)} testid="opt-notify" />}
                {fld("opt_future_projects") && <OptionalYesNo label="Would you like Mickey to contact you about future projects?" value={form.future_projects} onChange={v => up("future_projects", v)} testid="opt-future" />}
              </div>
            </Section>
          )}

          {/* Emergency Contact */}
          {sec("emergency_contact") && (
            <Section title={secTitle("emergency_contact") || "EMERGENCY CONTACT (OPTIONAL)"} testid="section-emergency">
              <div className="grid sm:grid-cols-3 gap-4">
                {fld("emergency_name") && <Field label="Name" value={form.emergency_name} onChange={v => up("emergency_name", v)} testid="field-emerg-name" />}
                {fld("emergency_phone") && <Field label="Phone" type="tel" value={form.emergency_phone} onChange={v => up("emergency_phone", v)} testid="field-emerg-phone" />}
                {fld("emergency_relationship") && <Field label="Relationship" value={form.emergency_relationship} onChange={v => up("emergency_relationship", v)} testid="field-emerg-rel" />}
              </div>
            </Section>
          )}

          {/* Digital Signature */}
          <Section title="DIGITAL SIGNATURE" testid="section-signature">
            <p className="text-sm text-gray-500 mb-4">
              Please sign below using your mouse, touchscreen, or stylus.
            </p>
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden relative bg-white mb-3" data-testid="signature-pad-wrapper">
              <SignatureCanvas
                ref={sigRef}
                penColor="black"
                canvasProps={{ className: "w-full", style: { height: 160, width: "100%" } }}
              />
              <button onClick={() => sigRef.current?.clear()}
                className="absolute top-2 right-2 text-gray-400 hover:text-black p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="clear-signature" type="button" title="Clear signature">
                <RotateCcw size={16} />
              </button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <Field label="Typed Name *" value={form.typed_name} onChange={v => up("typed_name", v)} testid="field-typed-name" />
              <Field label="Date" type="date" value={form.date} onChange={v => up("date", v)} testid="field-sig-date" disabled />
            </div>
            <p className="text-[10px] text-gray-400 mt-3">
              Your IP address, timestamp, and browser information will be captured automatically as part of the audit trail.
            </p>
          </Section>

          {/* Final Agreement */}
          <div className="border-t-2 border-black pt-8 mt-12" data-testid="section-final">
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
              <Checkbox
                label="I certify that I have read and understand this agreement. I voluntarily agree to participate in The Empty Chair™ Podcast."
                checked={form.final_agree}
                onChange={() => up("final_agree", !form.final_agree)}
                testid="check-final"
                bold
                large
              />
            </div>

            <button
              onClick={submit}
              disabled={submitting}
              className="w-full mt-8 bg-black text-white py-5 text-sm font-bold tracking-[0.3em] uppercase rounded-full hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="submit-release"
            >
              {submitting ? "Submitting..." : "Take a Seat"}
            </button>

            <p className="text-center text-xs text-gray-400 mt-6 tracking-wider">
              THANK YOU FOR BEING PART OF THE CONVERSATION.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* ─── Reusable Components ─── */

function Section({ num, title, testid, children }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <div className="border-t border-gray-200 pt-6 mb-4">
        <h3 className="text-sm font-bold tracking-[0.15em] uppercase text-black">
          {num ? `${num}. ` : ""}{title}
        </h3>
      </div>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testid, disabled = false }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.2em] uppercase text-gray-500 block mb-1.5">{label}</label>
      <input
        type={type} value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
        className="w-full border-b-2 border-gray-300 bg-transparent py-2.5 text-sm focus:border-black focus:outline-none transition-colors disabled:text-gray-400"
        data-testid={testid}
      />
    </div>
  );
}

function Checkbox({ label, checked, onChange, testid, bold = false, large = false }) {
  return (
    <label className={`flex items-start gap-3 cursor-pointer group ${large ? "py-1" : ""}`} data-testid={testid}>
      <div className={`${large ? "w-6 h-6 mt-0.5" : "w-5 h-5 mt-0.5"} rounded border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
        checked ? "bg-black border-black" : "border-gray-300 group-hover:border-gray-500"
      }`}>
        {checked && (
          <svg width={large ? 14 : 12} height={large ? 14 : 12} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
        )}
      </div>
      <input type="checkbox" checked={checked} onChange={onChange} className="hidden" />
      <span className={`text-sm leading-relaxed ${bold ? "font-semibold" : "text-gray-700"}`}>{label}</span>
    </label>
  );
}

function Radio({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group" data-testid={testid}>
      <div className={`w-5 h-5 mt-0.5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
        checked ? "border-black" : "border-gray-300 group-hover:border-gray-500"
      }`}>
        {checked && <div className="w-2.5 h-2.5 rounded-full bg-black" />}
      </div>
      <input type="radio" checked={checked} onChange={onChange} className="hidden" />
      <span className="text-sm text-gray-700 leading-relaxed">{label}</span>
    </label>
  );
}

function OptionalYesNo({ label, value, onChange, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-sm text-gray-700 mb-2">{label}</p>
      <div className="flex gap-4">
        {["yes", "no"].map(v => (
          <label key={v} className="flex items-center gap-2 cursor-pointer" onClick={() => onChange(v)}>
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              value === v ? "border-black" : "border-gray-300"
            }`}>
              {value === v && <div className="w-2 h-2 rounded-full bg-black" />}
            </div>
            <span className="text-sm capitalize">{v}</span>
          </label>
        ))}
      </div>
    </div>
  );
}
